import React from "react";

const TestComparativeAnalysis = () => {
  // Sample comparative analysis data that would come from the backend
  const sampleComparativeAnalysis = `- Left-leaning sources emphasize concerns about government overreach and civil liberties violations, highlighting potential impacts on marginalized communities and questioning the constitutional authority for such actions.

- Center/neutral sources focus on the legal and procedural aspects of the policy implementation, presenting both supporters' and critics' viewpoints while examining the practical implications for affected communities.

- Right-leaning sources emphasize public safety and law enforcement perspectives, highlighting the necessity of enforcing immigration laws and focusing on removing individuals with criminal records from communities.`;

  // Parse the comparative analysis (same logic as in StoryDetailPage)
  const parseComparativeAnalysis = (text) => {
    const sections = [];
    const lines = text.split("\n");
    let currentSection = null;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;

      // Handle format: "LEFT:" or "Left:"
      if (trimmedLine.match(/^(LEFT|CENTER|RIGHT|Left|Center|Right):/i)) {
        if (currentSection) sections.push(currentSection);

        const [perspective, ...contentParts] = trimmedLine.split(":");
        currentSection = {
          perspective: perspective.trim().toUpperCase(),
          points:
            contentParts.length > 0 ? [contentParts.join(":").trim()] : [],
        };
      }
      // Handle format: "- Left-leaning sources..." or "- Center/neutral sources..."
      else if (
        trimmedLine.match(
          /^-\s*(Left-leaning|Center\/neutral|Right-leaning|Left|Center|Right)/i
        )
      ) {
        if (currentSection) sections.push(currentSection);

        let perspective = "CENTER";
        if (trimmedLine.match(/Left-leaning|Left/i)) {
          perspective = "LEFT";
        } else if (trimmedLine.match(/Right-leaning|Right/i)) {
          perspective = "RIGHT";
        }

        // Extract content after the perspective identifier
        const content = trimmedLine
          .replace(
            /^-\s*(Left-leaning|Center\/neutral|Right-leaning)\s*sources?\s*/i,
            ""
          )
          .trim();

        currentSection = {
          perspective: perspective,
          points: content ? [content] : [],
        };
      }
      // Handle regular bullet points
      else if (trimmedLine.startsWith("-") && currentSection) {
        currentSection.points.push(trimmedLine.substring(1).trim());
      } else if (currentSection && currentSection.points.length === 0) {
        // First line after perspective header
        currentSection.points.push(trimmedLine);
      } else if (currentSection) {
        // Continue previous point
        const lastIndex = currentSection.points.length - 1;
        if (lastIndex >= 0) {
          currentSection.points[lastIndex] += " " + trimmedLine;
        }
      }
    }

    if (currentSection) sections.push(currentSection);
    return sections;
  };

  const sections = parseComparativeAnalysis(sampleComparativeAnalysis);

  const getPerspectiveColor = (perspective) => {
    switch (perspective) {
      case "LEFT":
        return {
          bg: "bg-blue-50",
          border: "border-blue-400",
          text: "text-blue-900",
          icon: "←",
        };
      case "CENTER":
        return {
          bg: "bg-green-50",
          border: "border-green-400",
          text: "text-green-900",
          icon: "⚖️",
        };
      case "RIGHT":
        return {
          bg: "bg-red-50",
          border: "border-red-400",
          text: "text-red-900",
          icon: "→",
        };
      default:
        return {
          bg: "bg-gray-50",
          border: "border-gray-400",
          text: "text-gray-900",
          icon: "•",
        };
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-100 min-h-screen">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🧪 Test: Three-Column Comparative Analysis UI
        </h1>
        <p className="text-gray-600">
          This demonstrates how the "⚡ Key Differences" section looks with the
          new three-column layout
        </p>
      </div>

      <div className="p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-neutral-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          ⚡ Key Differences - Media bias on this story
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Ensure we have all three perspectives in the right order */}
            {["LEFT", "CENTER", "RIGHT"].map((perspective) => {
              const section = sections.find(
                (s) => s.perspective === perspective
              );
              const colors = getPerspectiveColor(perspective);

              return (
                <div
                  key={perspective}
                  className={`p-4 ${colors.bg} rounded-lg border-l-4 ${colors.border} min-h-[200px]`}
                >
                  <h4
                    className={`text-sm font-semibold ${colors.text} mb-3 flex items-center justify-center text-center`}
                  >
                    <span className="mr-2 text-lg">{colors.icon}</span>
                    {perspective}-LEANING
                  </h4>
                  <div className="space-y-2">
                    {section ? (
                      section.points.map((point, pointIndex) => (
                        <div
                          key={pointIndex}
                          className={`${colors.text.replace("900", "800")} text-sm leading-relaxed`}
                        >
                          {point}
                        </div>
                      ))
                    ) : (
                      <div
                        className={`${colors.text.replace("900", "600")} text-sm italic text-center`}
                      >
                        No {perspective.toLowerCase()}
                        -leaning analysis available
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
        <h3 className="text-sm font-semibold text-yellow-900 mb-2">
          📝 How this works:
        </h3>
        <ul className="text-yellow-800 text-sm space-y-1">
          <li>
            • Uses the same prompt configuration from your admin dashboard
          </li>
          <li>
            • Parses text format: "- Left-leaning sources..." automatically
          </li>
          <li>• Displays in responsive three-column layout</li>
          <li>• Same styling and logic as the bias checker feature</li>
        </ul>
      </div>
    </div>
  );
};

export default TestComparativeAnalysis;
